import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  fetchTracingDepthCompletion,
  fetchTracingPoDocuments,
} from '@/api/report';
import { useSessionStore } from '@/store/sessionStore';

export function tracingDepthCompletionQueryOptions(
  companyId: string,
  filter: string,
) {
  return queryOptions({
    queryKey: [
      'report',
      'tracing-depth-completion',
      companyId,
      filter,
    ] as const,
    queryFn: () =>
      fetchTracingDepthCompletion(companyId, filter).then(({ data }) => data),
    enabled: !!companyId && !!filter,
  });
}

export function useTracingDepthCompletionQuery(filter: string) {
  const companyId = useSessionStore(s => s.user?.companyId ?? '');
  return useQuery(tracingDepthCompletionQueryOptions(companyId, filter));
}

export function useTracingPoDocumentsQuery(params: {
  filter: string;
  isMissing: boolean;
  page: number;
  count: number;
  sort?: string;
}) {
  const companyId = useSessionStore(s => s.user?.companyId ?? '');
  return useQuery({
    queryKey: [
      'report',
      'tracing-po-documents',
      companyId,
      params.filter,
      params.isMissing,
      params.page,
      params.count,
      params.sort ?? '',
    ] as const,
    queryFn: () => {
      const qs = new URLSearchParams({
        filter: params.filter,
        isMissing: String(params.isMissing),
        // The table's pages are 1-based, the API's 0-based.
        page: String(Math.max(0, params.page - 1)),
        count: String(params.count),
        ...(params.sort ? { sort: params.sort } : {}),
      });
      return fetchTracingPoDocuments(companyId, qs.toString());
    },
    select: res => ({
      data: res.data,
      pagination: {
        total: res.metadata.pagination.total,
        page: res.metadata.pagination.page,
        count: res.metadata.pagination.limit,
      },
    }),
    enabled: !!companyId && !!params.filter,
    placeholderData: prev => prev,
  });
}
